import { CARCASS_MM, HALF_CARCASS_MM } from "./carcass";
import type { InternalItem, WardrobeModule } from "../types";
import { cabinetBaseHeightMm } from "./installation";
import { cabinetDividerOffsets } from "./moduleRules";
import { cabinetInteriorFloorMm } from "./cabinetDrawers";
import { snap } from "./project";
import { fittingFitsWidth, fittingVerticalOffsets, NOMINAL_WIDTH_FITTINGS, nominalFittingHint } from "./wardrobeFittings";

/**
 * Внутреннее пространство корпуса делится на отсеки. Широкий шкаф — один
 * корпус с общими стойками, и каждая стойка идёт от дна до крышки. Поэтому
 * полка, ящик, корзина, штанга или пантограф не могут быть «на всю ширину
 * шкафа»: в каждом пролёте стоит своя деталь от стойки до стойки. Так же
 * делит пролёт перегородка, если она перекрывает деталь по высоте.
 *
 * Все размеры — мм, по горизонтали от центра корпуса, по вертикали от низа
 * корпуса. Этим пользуются 3D-сцена, техкарта, смета и проверки.
 */

export const PANEL_THICKNESS_MM = CARCASS_MM;
const HALF_PANEL_MM = PANEL_THICKNESS_MM / 2;
/** Зазор между перегородкой и боковиной или стойкой; у боковины он был и раньше. */
export const DIVIDER_CLEARANCE_MM = 39;
/** Детали, соприкасающиеся по высоте меньше чем на это, не пересекаются. */
const TOUCH_MM = 0.5;

/** Ширина в свету, меньше которой деталь не ставится. */
export const MIN_PIECE_WIDTH_MM: Record<InternalItem["type"], number> = {
  shelf: 100,
  led: 100,
  rail: 150,
  pantograph: 400,
  drawer: 250,
  basket: 250,
  leatherBasket: 250,
  shoeRack: 250,
  trouserRack: 500,
  divider: 0
};

export const defaultDividerHeightMm = (module: Pick<WardrobeModule, "height">) =>
  Math.min(1400, module.height - 180);

export interface Partition {
  /** Ось панели. */
  x: number;
  bottom: number;
  top: number;
  kind: "upright" | "divider";
  itemId?: string;
}

export interface ItemPiece {
  /** Лицевые плоскости боковин, стоек или перегородок по краям. */
  left: number;
  right: number;
  width: number;
  center: number;
}

const STRUCTURAL_ID_PREFIX = "structural-upright:";
const isStructural = (item: InternalItem) => item.id.startsWith(STRUCTURAL_ID_PREFIX);

/**
 * Внутренний объём для наполнения по высоте: от верха дна (или полки над
 * наружными ящиками) до низа крышки.
 */
export const interiorVerticalRangeMm = (module: WardrobeModule): [number, number] =>
  [cabinetInteriorFloorMm(module), module.height - PANEL_THICKNESS_MM];

/** Корпус изнутри по высоте целиком, вместе с отсеком наружных ящиков. */
const carcassVerticalRangeMm = (module: WardrobeModule): [number, number] => {
  const floorTop = (module.type === "mezzanine" ? 0 : cabinetBaseHeightMm(module)) + PANEL_THICKNESS_MM;
  return [floorTop, module.height - PANEL_THICKNESS_MM];
};

/** Лицевые плоскости боковин изнутри. */
export const interiorHorizontalRangeMm = (module: WardrobeModule): [number, number] =>
  [-module.width / 2 + PANEL_THICKNESS_MM, module.width / 2 - PANEL_THICKNESS_MM];

export const structuralPartitions = (module: WardrobeModule): Partition[] => {
  // Общие стойки идут от дна: ящики внизу стоят каждый в своём пролёте.
  const [bottom, top] = carcassVerticalRangeMm(module);
  return cabinetDividerOffsets(module).map(x => ({ x, bottom, top, kind: "upright" as const }));
};

export const dividerPartitions = (module: WardrobeModule): Partition[] =>
  module.items
    .filter(item => item.type === "divider" && !isStructural(item))
    .map(item => {
      const height = item.height ?? defaultDividerHeightMm(module);
      return { x: item.x ?? 0, bottom: item.y - height / 2, top: item.y + height / 2, kind: "divider" as const, itemId: item.id };
    });

export const modulePartitions = (module: WardrobeModule) =>
  [...structuralPartitions(module), ...dividerPartitions(module)];

/**
 * Для 3D-сцены: общие стойки добавляются к наполнению как сплошные
 * перегородки. Тогда штанги, обувницы, декор и одежда обходят стойку
 * тем же кодом, что и обычную перегородку. В проект это не сохраняется.
 */
export const withStructuralPartitions = (module: WardrobeModule): WardrobeModule => {
  const uprights = structuralPartitions(module);
  if (!uprights.length) return module;
  return {
    ...module,
    items: [
      ...module.items,
      ...uprights.map((upright, index): InternalItem => ({
        id: `${STRUCTURAL_ID_PREFIX}${index}`,
        type: "divider",
        x: upright.x,
        y: (upright.bottom + upright.top) / 2,
        height: upright.top - upright.bottom
      }))
    ]
  };
};

/** Габарит детали по высоте. У штанги — сама штанга, одежда считается отдельно. */
export const internalItemSpanMm = (module: WardrobeModule, item: InternalItem): [number, number] => {
  switch (item.type) {
    case "shelf":
    case "led":
      return [item.y - HALF_CARCASS_MM, item.y + HALF_CARCASS_MM];
    case "rail":
      return [item.y - 14, item.y + 14];
    case "drawer":
      return [item.y - (item.height ?? 180), item.y];
    case "basket":
      return [item.y - (item.height ?? 160), item.y];
    case "shoeRack":
    case "leatherBasket":
    case "trouserRack": {
      const [from, to] = fittingVerticalOffsets(item.type, item.height)!;
      return [item.y + from, item.y + to];
    }
    case "pantograph":
      return [item.y - 390, item.y + 20];
    case "divider": {
      const height = item.height ?? defaultDividerHeightMm(module);
      return [item.y - height / 2, item.y + height / 2];
    }
  }
};

const overlapsVertically = (span: [number, number], partition: Partition) =>
  span[0] < partition.top - TOUCH_MM && span[1] > partition.bottom + TOUCH_MM;

/**
 * Отсеки, в которых стоит деталь: пространство между боковинами, разрезанное
 * стойками и перегородками, которые перекрывают деталь по высоте.
 * Возвращаются все отсеки, в том числе слишком узкие; см. usableItemPieces.
 */
export const internalItemPieces = (module: WardrobeModule, item: InternalItem): ItemPiece[] => {
  if (item.type === "divider" || module.type === "corner45") {
    const [left, right] = interiorHorizontalRangeMm(module);
    return [{ left, right, width: right - left, center: (left + right) / 2 }];
  }
  const span = internalItemSpanMm(module, item);
  const [left, right] = interiorHorizontalRangeMm(module);
  const cuts = modulePartitions(module)
    .filter(partition => overlapsVertically(span, partition))
    .map(partition => [partition.x - HALF_PANEL_MM, partition.x + HALF_PANEL_MM] as const)
    .sort((a, b) => a[0] - b[0]);
  const pieces: ItemPiece[] = [];
  let cursor = left;
  for (const [from, to] of cuts) {
    const end = Math.min(from, right);
    if (end > cursor) pieces.push({ left: cursor, right: end, width: end - cursor, center: (cursor + end) / 2 });
    cursor = Math.max(cursor, to);
  }
  if (right > cursor) pieces.push({ left: cursor, right, width: right - cursor, center: (cursor + right) / 2 });
  return anchoredPieces(pieces, item);
};

/** Деталь одного отсека: отсек, где её точка, иначе ближайший (если стойки сдвинулись). */
const anchoredPieces = (pieces: ItemPiece[], item: InternalItem) => {
  if (!Number.isFinite(item.anchorX) || !pieces.length) return pieces;
  const x = Number(item.anchorX);
  const inside = pieces.find(piece => x >= piece.left - 0.5 && x <= piece.right + 0.5);
  return [inside ?? [...pieces].sort((a, b) => Math.abs(a.center - x) - Math.abs(b.center - x))[0]!];
};

/** Стоят ли две детали хотя бы в одном общем отсеке (по горизонтали). */
export const itemsShareCompartment = (module: WardrobeModule, first: InternalItem, second: InternalItem) => {
  if (first.type === "divider" || second.type === "divider") return false;
  const a = internalItemPieces(module, first);
  const b = internalItemPieces(module, second);
  return a.some(one => b.some(other => one.left < other.right - 1 && other.left < one.right - 1));
};

/**
 * Разделить строку наполнения на отдельные детали по отсекам: каждая стоит
 * в своём отсеке и двигается независимо. Деталь в отсеке keepX сохраняет id.
 */
export const splitItemByCompartments = (
  module: WardrobeModule,
  item: InternalItem,
  keepX: number,
  createItemId: () => string
): InternalItem[] => {
  if (item.type === "divider" || Number.isFinite(item.anchorX)) return [item];
  const pieces = usableItemPieces(module, item);
  if (pieces.length < 2) return [item];
  const kept = pieces.find(piece => keepX >= piece.left - 0.5 && keepX <= piece.right + 0.5)
    ?? [...pieces].sort((a, b) => Math.abs(a.center - keepX) - Math.abs(b.center - keepX))[0]!;
  return pieces.map(piece => ({
    ...item,
    id: piece === kept ? item.id : createItemId(),
    anchorX: Math.round(piece.center)
  }));
};

/** «левый отсек», «средний», «правый» или «отсек 2 из 4» — для списка наполнения. */
export const itemCompartmentLabel = (module: WardrobeModule, item: InternalItem) => {
  if (!Number.isFinite(item.anchorX) || item.type === "divider") return null;
  const all = internalItemPieces(module, { ...item, anchorX: undefined });
  if (all.length < 2) return null;
  const [own] = internalItemPieces(module, item);
  const index = all.findIndex(piece => own && Math.abs(piece.center - own.center) < 1);
  if (index < 0) return null;
  if (all.length === 2) return index === 0 ? "левый отсек" : "правый отсек";
  if (all.length === 3) return ["левый отсек", "средний отсек", "правый отсек"][index]!;
  return `отсек ${index + 1} из ${all.length}`;
};

/** Отсеки, куда деталь действительно ставится. */
export const usableItemPieces = (module: WardrobeModule, item: InternalItem) =>
  internalItemPieces(module, item).filter(piece => fittingFitsWidth(item.type, piece.width, MIN_PIECE_WIDTH_MM[item.type]));

/** Сколько физических деталей даёт одна строка наполнения. */
export const internalItemPieceCount = (module: WardrobeModule, item: InternalItem) =>
  item.type === "divider" ? 1 : usableItemPieces(module, item).length;

/** Допустимые положения оси перегородки: не ближе зазора к боковинам и стойкам. */
export const dividerXIntervals = (module: WardrobeModule): Array<[number, number]> => {
  const reach = HALF_PANEL_MM + DIVIDER_CLEARANCE_MM;
  const [left, right] = interiorHorizontalRangeMm(module);
  const blocked = cabinetDividerOffsets(module)
    .map(x => [x - HALF_PANEL_MM - reach, x + HALF_PANEL_MM + reach] as const);
  const intervals: Array<[number, number]> = [];
  let cursor = left + reach;
  for (const [from, to] of blocked) {
    if (from > cursor) intervals.push([cursor, from]);
    cursor = Math.max(cursor, to);
  }
  if (right - reach >= cursor) intervals.push([cursor, right - reach]);
  return intervals.length ? intervals : [[0, 0]];
};

/** Перегородка не встаёт на стойку и не прижимается к ней вплотную. */
export const clampDividerX = (module: WardrobeModule, desired: number) => {
  const intervals = dividerXIntervals(module);
  let best = desired;
  let bestDistance = Infinity;
  for (const [from, to] of intervals) {
    const candidate = Math.min(to, Math.max(from, desired));
    const distance = Math.abs(candidate - desired);
    if (distance < bestDistance) { best = candidate; bestDistance = distance; }
  }
  const [from, to] = intervals.find(([a, b]) => best >= a && best <= b)!;
  const snapped = snap(best);
  return snapped >= from && snapped <= to ? snapped : Math.round(best);
};

/** Где ставить новую перегородку: посередине первого пролёта. */
export const defaultDividerX = (module: WardrobeModule) => {
  const offsets = cabinetDividerOffsets(module);
  if (!offsets.length) return 0;
  const [left] = interiorHorizontalRangeMm(module);
  return clampDividerX(module, (left + offsets[0]! - HALF_PANEL_MM) / 2);
};

export interface InternalConflict {
  itemId: string;
  kind: "divider-on-upright" | "divider-at-side" | "piece-too-narrow" | "no-room";
  text: string;
}

/**
 * Проверка совместимости наполнения с конструкцией корпуса. Пустой список —
 * всё собирается: ни одна деталь не проходит сквозь стойку или перегородку и
 * в каждом отсеке хватает ширины.
 */
export const internalLayoutConflicts = (module: WardrobeModule): InternalConflict[] => {
  if (module.type === "shelving" || module.type === "end" || module.type === "corner45") return [];
  const conflicts: InternalConflict[] = [];
  const intervals = dividerXIntervals(module);
  const uprights = cabinetDividerOffsets(module);
  for (const partition of dividerPartitions(module)) {
    const allowed = intervals.some(([from, to]) => partition.x >= from - 0.5 && partition.x <= to + 0.5);
    if (allowed) continue;
    const onUpright = uprights.some(x => Math.abs(partition.x - x) < PANEL_THICKNESS_MM + DIVIDER_CLEARANCE_MM);
    conflicts.push(onUpright
      ? { itemId: partition.itemId!, kind: "divider-on-upright", text: "перегородка стоит на общей стойке корпуса или вплотную к ней" }
      : { itemId: partition.itemId!, kind: "divider-at-side", text: `перегородка ближе ${DIVIDER_CLEARANCE_MM} мм к боковине` });
  }
  for (const item of module.items) {
    if (item.type === "divider" || isStructural(item)) continue;
    const pieces = internalItemPieces(module, item);
    const usable = pieces.filter(piece => fittingFitsWidth(item.type, piece.width, MIN_PIECE_WIDTH_MM[item.type]));
    if (!usable.length) {
      conflicts.push({ itemId: item.id, kind: "no-room", text: NOMINAL_WIDTH_FITTINGS.has(item.type)
        ? nominalFittingHint(item.type).replace(/^./, (letter) => letter.toLowerCase())
        : `для элемента нет отсека шире ${MIN_PIECE_WIDTH_MM[item.type]} мм` });
    } else if (usable.length < pieces.length) {
      const narrow = pieces.filter(piece => !usable.includes(piece)).map(piece => Math.round(piece.width));
      conflicts.push({ itemId: item.id, kind: "piece-too-narrow", text: `в отсеке ${narrow.join(", ")} мм элемент не помещается и не ставится` });
    }
  }
  return conflicts;
};
